import React, { useState } from 'react';
import { CheckCircle, XCircle, Flag, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

interface ApprovalActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (action: 'approve' | 'reject' | 'flag', reason?: string) => void;
  entityType: 'gallery' | 'image';
  entityTitle?: string;
  defaultAction?: 'approve' | 'reject' | 'flag';
  isLoading?: boolean;
}

const actionConfig = {
  approve: {
    icon: CheckCircle,
    label: 'Approve',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    description: 'Mark this item as approved and ready for use',
    buttonColor: 'bg-green-600 hover:bg-green-700',
    reasonRequired: false,
    reasonLabel: 'Approval notes (optional)',
    reasonPlaceholder: 'Add any notes about this approval...'
  },
  reject: {
    icon: XCircle,
    label: 'Reject',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    description: 'Reject this item and prevent it from being used',
    buttonColor: 'bg-red-600 hover:bg-red-700',
    reasonRequired: true,
    reasonLabel: 'Rejection reason (required)',
    reasonPlaceholder: 'Please explain why this item is being rejected...'
  },
  flag: {
    icon: Flag,
    label: 'Flag for Review',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    description: 'Flag this item for additional review or attention',
    buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
    reasonRequired: false,
    reasonLabel: 'Flag reason (optional)',
    reasonPlaceholder: 'Describe what needs attention...'
  }
} as const;

const predefinedReasons = {
  reject: [
    'Poor image quality',
    'Inappropriate content',
    'Incorrect or missing information',
    'Does not meet brand standards',
    'Copyright concerns',
    'Safety issues visible',
    'Other (specify below)'
  ],
  flag: [
    'Needs additional review',
    'Quality concerns',
    'Requires client approval',
    'Potential copyright issue',
    'Technical issues',
    'Other (specify below)'
  ]
};

export const ApprovalActionDialog: React.FC<ApprovalActionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  entityType,
  entityTitle,
  defaultAction = 'approve',
  isLoading = false
}) => {
  const [selectedAction, setSelectedAction] = useState<'approve' | 'reject' | 'flag'>(defaultAction);
  const [reason, setReason] = useState('');
  const [predefinedReason, setPredefinedReason] = useState('');

  const config = actionConfig[selectedAction];
  const Icon = config.icon;
  const showPredefinedReasons = selectedAction !== 'approve' && predefinedReasons[selectedAction];
  const finalReason = predefinedReason === 'Other (specify below)' ? reason : predefinedReason || reason;
  const isValid = !config.reasonRequired || finalReason.trim().length > 0;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm(selectedAction, finalReason.trim() || undefined);
    handleClose();
  };

  const handleClose = () => {
    setReason('');
    setPredefinedReason('');
    setSelectedAction(defaultAction);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${config.color}`} />
            {config.label} {entityType}
          </DialogTitle>
          <DialogDescription>
            {entityTitle && (
              <div className="mb-2 font-medium text-gray-900">
                {entityTitle}
              </div>
            )}
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Action Selection */}
          <div>
            <Label className="text-sm font-medium">Action</Label>
            <RadioGroup 
              value={selectedAction} 
              onValueChange={(value) => setSelectedAction(value as typeof selectedAction)}
              className="mt-2"
            >
              {Object.entries(actionConfig).map(([action, cfg]) => {
                const ActionIcon = cfg.icon;
                return (
                  <div key={action} className="flex items-center space-x-2">
                    <RadioGroupItem value={action} id={action} />
                    <Label 
                      htmlFor={action}
                      className={`flex items-center gap-2 cursor-pointer ${cfg.color}`}
                    >
                      <ActionIcon className="h-4 w-4" />
                      {cfg.label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Predefined Reasons */}
          {showPredefinedReasons && (
            <div>
              <Label className="text-sm font-medium">
                Select reason
              </Label>
              <RadioGroup 
                value={predefinedReason} 
                onValueChange={setPredefinedReason}
                className="mt-2"
              >
                {predefinedReasons[selectedAction].map((reasonOption) => (
                  <div key={reasonOption} className="flex items-center space-x-2">
                    <RadioGroupItem value={reasonOption} id={reasonOption} />
                    <Label htmlFor={reasonOption} className="text-sm cursor-pointer">
                      {reasonOption}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Custom Reason/Notes */}
          {(selectedAction === 'approve' || predefinedReason === 'Other (specify below)' || !showPredefinedReasons) && (
            <div>
              <Label htmlFor="reason" className="text-sm font-medium">
                {config.reasonLabel}
                {config.reasonRequired && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={config.reasonPlaceholder}
                className="mt-2"
                rows={3}
              />
              {config.reasonRequired && !finalReason.trim() && (
                <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                  <AlertTriangle className="h-3 w-3" />
                  This field is required
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className={`text-white ${config.buttonColor}`}
          >
            {isLoading ? 'Processing...' : config.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalActionDialog;